import React, { useContext } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { t } from '@lingui/macro';
import { isFuture } from 'date-fns';
import { Container, Flex } from '@components/layout/Grid';
import useFlags from '@lib/hooks/useFlags';
import LanguageContext from '@lib/contexts/languageContext';
import { UserContext } from '@lib/contexts/UserProvider';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  column-gap: 15px;
  row-gap: 15px;

  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    flex-direction: row;
  }
`;

const Items = styled.div`
  flex: 1;
  display: grid;
  grid-gap: 15px;

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    grid-template-columns: repeat(2, minmax(250px, 50%));
  }
`;

const Image = styled.img`
  max-width: 100%;
  object-fit: cover;
`;

const GridElement = styled(Link)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  background-color: ${({ theme }) => theme.colors.neutrals['light-gray']};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: ${({ theme }) => theme.viewports.mobile}px) {
    margin-bottom: 15px;

    :nth-of-type(4) {
      display: none;
    }
  }

  @media (min-width: ${({ theme }) => theme.viewports.mobile}px) {
    flex-direction: row;
  }
`;

// UTC time
const start_date = new Date(Date.UTC(2021, 10, 14, 23));
const end_date = new Date(Date.UTC(2021, 11, 9, 23));
const showCoursesSale = !isFuture(start_date) && isFuture(end_date);

const images = {
  program: {
    se: '/static/landing/65a4a761-b5c2-4e2c-bb60-61a71131e2c7.jpeg',
  },
  challenge: {
    se: '/static/landing/big_square_challenge_se.jpg',
    no: '/static/landing/big_square_challenge_no.jpg',
    en: '/static/landing/big_square_challenge_en.jpg',
    fi: '/static/landing/big_square_challenge_fi.jpg',
  },
  live: {
    se: '/static/landing/small_square_live_se.jpg',
    no: '/static/landing/small_square_live_no.jpg',
    en: '/static/landing/small_square_live_en.jpg',
    fi: '/static/landing/small_square_live_fi.jpg',
  },
  get_started: {
    se: '/static/landing/small_square_get_started_se.jpg',
    no: '/static/landing/small_square_get_started_no.jpg',
    en: '/static/landing/small_square_get_started_en.jpg',
    fi: '/static/landing/small_square_get_started_fi.jpg',
  },
  yogobe_team: {
    se: '/static/landing/small_square_team_se.jpg',
    no: '/static/landing/small_square_team_no.jpg',
    en: '/static/landing/small_square_team_en.jpg',
    fi: '/static/landing/small_square_team_fi.jpg',
  },
  courses: {
    se: showCoursesSale
      ? '/static/landing/courses_offer_se.jpeg'
      : '/static/landing/small_square_online_courses_se.jpg',
    no: '/static/landing/small_square_online_courses_no.jpg',
    en: '/static/landing/small_square_online_courses_en.jpg',
    fi: '/static/landing/small_square_online_courses_fi.jpg',
  },
};

const SquaresGrid = () => {
  const locale = useContext(LanguageContext);
  const [flags, { loading }] = useFlags();
  const lang = ['en', 'fi'].includes(locale) ? 'en' : 'se';
  const [currentUser] = useContext(UserContext);

  let showYearlyCampaign = true;
  if (
    // user not loaded yet
    currentUser === undefined ||
    // user doesnt have trial premium
    (currentUser?.subscription?.status === 'active' &&
      ['monthly', 'quarterly', 'yearly'].includes(
        currentUser?.subscription?.currentSubscriptionPlan?.planType
      )) ||
    // has wellness
    currentUser?.hasWellness
  ) {
    showYearlyCampaign = false;
  }

  // default
  let banner = null;
  if (!loading) {
    if (flags.yearly_campaign_banners && showYearlyCampaign) {
      banner = (
        <GridElement href="/se/yearly">
          <Image
            src={
              ['en', 'fi'].includes(lang)
                ? '/static/yearly/EN_Banner_Startpage_Square_b.jpg'
                : '/static/yearly/SE_Banner_Startpage_Square_b.jpg'
            }
            loading="lazy"
          />
        </GridElement>
      );
    } else if (!flags.yearly_campaign_banners && flags?.yearly_campaign) {
      banner = (
        <GridElement href={`/${lang}/c/yogobe/b/spring-boost`}>
          <Image
            src={
              ['en', 'fi'].includes(lang)
                ? '/static/banner_springboost_en_startpage_square.jpg'
                : '/static/banner_springboost_startpage_square.jpg'
            }
            loading="lazy"
          />
        </GridElement>
      );
    } else if (['se', 'no'].includes(locale)) {
      banner = (
        <GridElement href={`/${locale}/c/yogobe/b/spring-boost`}>
          <Image
            src="/static/Campaign banner_Start page_1_1.jpg"
            // alt="Skapa nya, hållvara vanor i sommar – gå med i 16 Weeks of Well."
            loading="lazy"
          />
        </GridElement>
      );
    }
  }

  return (
    <Container flexDirection="column" px={[25, 20, 20]} py={40}>
      <Content>
        <Flex flex={1} mb={[15, 15, 0]} mr={[0, 0, 15]}>
          {banner}
        </Flex>
        <Items>
          <GridElement
            title={t`landingpage.squareitems.alt_text.live`}
            href={`/${lang}/live`}
          >
            <Image
              src={images.live[lang]}
              alt={t`landingpage.squareitems.alt_text.live`}
              height="480px"
              width="480px"
            />
          </GridElement>

          <GridElement
            title={t`landingpage.squareitems.alt_text.getstarted`}
            href={`/${lang}/traning-yoga-hemma`}
          >
            <Image
              src={images.get_started[lang]}
              alt={t`landingpage.squareitems.alt_text.getstarted`}
              height="480px"
              width="480px"
            />
          </GridElement>
          <GridElement
            title={t`landingpage.squareitems.alt_text.ybteam`}
            href={`/${lang}/team`}
          >
            <Image
              src={images.yogobe_team[lang]}
              alt={t`landingpage.squareitems.alt_text.ybteam`}
              height="480px"
              width="480px"
            />
          </GridElement>
          <GridElement
            title={t`landingpage.squareitems.alt_text.deepen_your_knowledge`}
            href={`/${lang}/marketplace/sales`}
          >
            <Image
              src={images.courses[lang]}
              alt={t`landingpage.squareitems.alt_text.deepen_your_knowledge`}
              height="480px"
              width="480px"
            />
          </GridElement>
        </Items>
      </Content>
    </Container>
  );
};

export default SquaresGrid;
